import { Injectable } from '@angular/core';

// services
import { TagsService } from '@app/tags/services/tags.service';
import { NgxHttpClient } from '@app/shared/ngx-http-client';

// api
import {
  FeaturedCategories,
  PartialUserPosition,
  SimilarSkillsResponse,
} from '@app/onboarding/onboarding-api.model';
import { SkillItem, Skill } from '@app/onboarding/onboarding.model';

// other
import { map, catchError, switchMap } from 'rxjs/operators';
import {
  Observable,
  combineLatest,
  throwError,
  of,
  BehaviorSubject,
} from 'rxjs';
import { AuthService } from '@app/shared/services/auth.service';
import { TargetsService } from '@app/shared/services/targets.service';
import { TranslateService } from '@ngx-translate/core';
import { TitleSuggestion } from '@app/target/target-api.model';
import { OnboardingStateService } from '@app/onboarding/services/onboarding-state.service';
import { TagsApi } from '@app/tags/tag-api.model';
import { DgError } from '@app/shared/models/dg-error';

export interface JobRole extends TitleSuggestion {
  featured?: boolean;
  selected?: boolean;
  resourceType?: string;
}
@Injectable({
  providedIn: 'root',
})
export class OnboardingService {
  /** Subscribe to this observable to get the latest featured tiles and details skills */
  public featuredTargetTitles: Observable<FeaturedCategories[]> =
    this.getFeaturedTargetTitles();
  public refresh$ = new BehaviorSubject<Skill[]>(null);

  /** Subscribe to this observable to get the latest user skills */
  public userTags$ = this.refresh$.pipe(switchMap(() => this.getUserTags()));
  constructor(
    private http: NgxHttpClient,
    private tagsService: TagsService,
    private authService: AuthService,
    private targetsService: TargetsService,
    private translateService: TranslateService,
    private onboardingStateService: OnboardingStateService
  ) {
    // Init the featured roles
    this.getFeaturedDisplayRoles().subscribe();
    this.onboardingStateService.currentRole$.subscribe();
  }

  /**
   * Add user positions for the user
   * @param userPositions user positions to be added
   */
  public addUserExperiences(
    userPositions: PartialUserPosition[] = []
  ): Observable<any> {
    return this.http
      .post('/userinputs/addorupdateuserpositions', userPositions)
      .pipe(
        catchError((error) =>
          throwError(
            new DgError(
              this.translateService.instant('Onboarding_SavingError', error)
            )
          )
        )
      );
  }

  /**
   * Load more similar skills to the similar displayed list
   */
  public loadMoreSimilarSkills(): void {
    const tags = this.onboardingStateService.similarTags;
    if (tags) {
      // take the next 5 skills to add to the featured skills display list
      this.onboardingStateService.addDisplayedSimilarSkills(
        tags.slice(0, tags.length >= 5 ? 5 : tags.length)
      );
      // remove the next five skills from the similar tags list
      this.onboardingStateService.updateSimilarTags(
        tags.slice(tags.length >= 5 ? 5 : tags.length)
      );
    }
  }

  /**
   * Add a new role to the list of displayed featured roles.
   *
   * @param newRole Job role to add to featured lsit of roles
   */
  public addFeaturedRole(newRole: JobRole): void {
    // deselect roles before adding new role
    const roles: JobRole[] =
      this.onboardingStateService.featuredRoleTargets.map((role) => ({
        ...role,
        selected: false,
      }));
    const roleFoundIndex = roles.findIndex((role) => role.id === newRole.id);
    if (roleFoundIndex < 0) {
      roles.unshift(newRole);
      this.onboardingStateService.featuredRoleTargetsChange(roles);
    } else {
      // If role is already in display list update to top of the list
      roles.splice(roleFoundIndex, 1);
      roles.unshift(newRole);
      this.onboardingStateService.featuredRoleTargetsChange(roles);
    }
  }

  /**
   * Add a skil to the list of skills the user has added on the review page
   * from search of the suggested skills
   *
   * @param skill to add to the list
   */
  public addUserAddedSkill(skill: Skill): void {
    const addedSkills = this.onboardingStateService.addedTags
      ? this.onboardingStateService.addedTags
      : [];

    const skillAlreadyAdded = addedSkills.some((tag) => {
      return (
        (!!tag.tagId && tag.tagId === skill.tagId) ||
        (!!tag.name && tag.name === skill.name)
      );
    });
    if (!skillAlreadyAdded) {
      addedSkills.unshift(skill);
      this.onboardingStateService.addSkillsChange([...addedSkills]);
    }
  }

  /**
   * Update a skill added from the review skill search or similarity
   * Ex: update a skill that has a rating added to it
   *
   * @param skill to update in the user added list
   *
   */
  public updateAddedSkill(skill: TagsApi.Tag): void {
    const skills = this.onboardingStateService.addedTags;
    const skillIndex = skills.findIndex((tag) => tag.tagId === skill.tagId);

    if (skillIndex > -1) {
      skills[skillIndex] = skill;
      this.onboardingStateService.addSkillsChange(skills);
    }
  }

  /**
   * Update a skill added from the users role, assigned, or resume
   * Ex: update a skill that has a rating added to it
   *
   * @param skill to update in the user added list
   *
   */
  public updateOnboardingUserAddedTagsSkill(skill: TagsApi.Tag): void {
    const skills = this.onboardingStateService.onboardingUserAddedTags;
    const skillIndex = skills.findIndex((tag) => tag.tagId === skill.tagId);

    if (skillIndex > -1) {
      skills[skillIndex] = skill;
      this.onboardingStateService.onboardingUserAddedTagsChange(skills);
    }
  }

  /**
   * Get the featured skills list
   * @param skillInput
   */
  public updateSimilarSkills(skillInput?: Skill[]): void {
    const onboardingUserAddedTags: Skill[] =
      this.onboardingStateService.onboardingUserAddedTags; // roles, assigned, resume,
    const profileTags: Skill[] =
      this.onboardingStateService.currentProfileSkills; // profile
    const addedTags: Skill[] = this.onboardingStateService.addedTags; // search & suggested
    const allTags: Skill[] = [
      ...profileTags,
      ...onboardingUserAddedTags,
      ...addedTags,
    ];
    if (
      this.onboardingStateService.similarTags?.length === 0 &&
      this.onboardingStateService.displayedSimilarTags?.length === 0 &&
      (skillInput || allTags?.length)
    ) {
      // If there are not previous skills, on first skill search add get similar skills
      // only get first 40
      const skillNames = skillInput
        ? skillInput.map((skill) => skill.name)
        : allTags.map((skill) => skill.name);
      this.getSimilarSkills(skillNames, allTags);
    }
  }

  /**
   * Get the featured organizations categories and tags
   */
  public getFeaturedCategories(): Observable<FeaturedCategories[]> {
    return combineLatest([
      this.featuredTargetTitles,
      this.getUserTopTags(),
    ]).pipe(
      map(([categories, tags]) => {
        // Get the list of current user tags
        const userTags = {} as Record<string, number>;
        for (const tag of tags) {
          userTags[this.normalize(tag.name)] = tag.tagId;
        }

        // If matching any in our list, mark those as Selected
        for (const category of categories) {
          for (const skillItem of category.detailedSkills) {
            const id = userTags[this.normalize(skillItem.name)];
            if (id !== undefined) {
              skillItem.selected = true;
              skillItem.tagId = id;
            } else {
              skillItem.selected = false;
            }
          }
        }

        return categories;
      })
    );
  }

  /**
   * Edit the parsed resume job role.
   */
  public editJobRole(
    oldlJobRole: PartialUserPosition,
    newJob: PartialUserPosition
  ): void {
    if (this.onboardingStateService.currentResume?.userPositions) {
      const jobFoundIndex =
        this.onboardingStateService.currentResume.userPositions.findIndex(
          (jobRole) =>
            jobRole.title === oldlJobRole.title &&
            jobRole.organizationName === oldlJobRole.organizationName &&
            jobRole.startDate === oldlJobRole.startDate
        );
      if (jobFoundIndex > -1) {
        // if updated tags don't match the old role
        if (
          this.onboardingStateService.currentResume.userPositions[jobFoundIndex]
            .tags !== newJob.tags
        ) {
          // remove the skills from the resume [skills] prop
          this.onboardingStateService.currentResume.userPositions[
            jobFoundIndex
          ].tags.forEach((tag) => {
            this.removeSkillFromResume(tag);
          });
          // add the new skills to the resume [skills] prop
          this.addSkillsToResume(newJob.tags);
        }

        this.onboardingStateService.currentResume.userPositions[jobFoundIndex] =
          newJob;
        this.onboardingStateService.updateResume(
          this.onboardingStateService.currentResume
        );
      }
    }
  }

  /**
   * Remove a skill from the list of skills added during the review page from search or skill similarity
   */
  public removeAddSkill(skill: Skill): void {
    const tags = this.onboardingStateService.addedTags;
    const index = tags.findIndex((tag) => tag.tagId === skill.tagId);
    if (index > -1) {
      tags.splice(index, 1);
      this.onboardingStateService.addSkillsChange(tags);
    }
  }

  /**
   * Remove a skill from added tags from the user's profile, role, or resume
   */
  public removeOnboardingUserAddedTag(skill: Skill): void {
    const tags = this.onboardingStateService.onboardingUserAddedTags;
    const index = tags.findIndex((tag) => tag.tagId === skill.tagId);
    if (index > -1) {
      tags.splice(index, 1);
      this.onboardingStateService.onboardingUserAddedTagsChange(tags);
    }
  }

  /**
   * Remove the user's job experience from uploaded resume.
   *
   * @param job Job experience to remove.
   */
  public removeJobRole(job: PartialUserPosition): void {
    if (this.onboardingStateService.currentResume?.userPositions) {
      const jobFoundIndex =
        this.onboardingStateService.currentResume.userPositions.findIndex(
          (jobRole) =>
            jobRole.title === job.title &&
            jobRole.organizationName === job.organizationName &&
            jobRole.startDate === job.startDate
        );
      if (jobFoundIndex > -1) {
        this.onboardingStateService.currentResume.userPositions[
          jobFoundIndex
        ].tags.forEach((tag) => {
          this.removeSkillFromResume(tag);
        });
        this.onboardingStateService.currentResume.userPositions.splice(
          jobFoundIndex,
          1
        );
        this.onboardingStateService.updateResume(
          this.onboardingStateService.currentResume
        );
      }
    }
  }

  /**
   * Remove a skill for the users resume experience job role
   *
   * @param job resume experience job role to remove skill from
   * @param skill skill to remove from resume experience job roll
   *
   */
  public removeJobRoleSkill(
    job: PartialUserPosition,
    skill: TagsApi.Tag
  ): void {
    // right now using jobtitle & organization
    // 1 Skills should flow through to the review table.
    // 2 If a user removes a skill it wont flow to the review table.
    // 3 If a user deletes an experience then the skills will NOT flow to the table .
    // 4 Every skill that flows to that review table will be added to the users profile if they don't remove it from the table
    // 5 Those experiences (if not deleted) should be added to the users profile
    // 6 If 2 experiences the same skill (ie leadership) and the user deletes one of the experiences or deletes the shared skill but the skill still exists in another experience then the skill will still flow to the review table.
    // 7 "skills" object that contains all skills in jobs/courses/degreed/resume it self. We aren't displaying courses/degrees anywhaere so there is no place to remove skills associated with those on the resume page. All these skills should be added to the review table
    if (this.onboardingStateService.currentResume?.userPositions) {
      const jobFoundIndex =
        this.onboardingStateService.currentResume.userPositions.findIndex(
          (jobRole) =>
            jobRole.title === job.title &&
            jobRole.organizationName === job.organizationName &&
            jobRole.startDate === job.startDate
        );
      if (jobFoundIndex > -1) {
        const skillFoundIndex =
          this.onboardingStateService.currentResume.userPositions[
            jobFoundIndex
          ].tags.findIndex((skillMatch) => skillMatch.tagId === skill.tagId);
        let jobSkills = [];
        // join all skills
        this.onboardingStateService.currentResume.userPositions.map((job) => {
          jobSkills = [...jobSkills, ...job.tags];
        });
        // check how many times it is containted in list
        if (skillFoundIndex > -1) {
          // if there is just 1 occurence in the list of all job skills remove it from the overall skills list
          this.removeSkillFromResume(skill);
          // remove the skill from the current job role
          this.onboardingStateService.currentResume.userPositions[
            jobFoundIndex
          ].tags.splice(skillFoundIndex, 1);
          // update the resume
          this.onboardingStateService.updateResume(
            this.onboardingStateService.currentResume
          );
        }
      }
    }
  }

  /**
   * Get the users interest skills the user is already following on profile
   */
  public getUserTags(): Observable<Skill[]> {
    return this.http
      .get('/user/getuserinterests', {
        params: { includeRatings: true },
      })
      .pipe(
        map((tags: Skill[]) => {
          // here emit user selected tags
          return tags.map((tag: Skill) => ({ ...tag, isFollowing: true }));
        }),
        catchError((error) =>
          throwError(
            new DgError(
              this.translateService.instant('TagsSvc_SkillTagsError', error)
            )
          )
        )
      );
  }

  /**
   * Search for an org role
   */
  public searchOrgRoles(term: string = ''): Observable<any> {
    // only search if there is a term
    if (term) {
      return this.targetsService.getSuggestedTitles('JobRole', term, 5);
    }
    return of(undefined);
  }

  /**
   * Add new skills to the [skills] property to the current resume
   */
  private addSkillsToResume(skills: TagsApi.Tag[] = []): void {
    if (this.onboardingStateService.currentResume?.skills) {
      const ids = new Set(
        this.onboardingStateService.currentResume.skills.map((d) => d.tagId)
      );
      // filter out any existing tags
      skills = skills.filter((tag) => !ids.has(tag.tagId));
      this.onboardingStateService.currentResume.skills = [
        ...this.onboardingStateService.currentResume.skills,
        ...skills,
      ];
      this.onboardingStateService.updateResume(
        this.onboardingStateService.currentResume
      );
    }
  }

  /**
   * Remove a skills from the current resume all [skills] property
   */
  private removeSkillFromResume(skill): void {
    let jobSkills = [];
    // join all skills
    this.onboardingStateService.currentResume.userPositions.map((job) => {
      jobSkills = [...jobSkills, ...job.tags];
    });
    // get the occurrences of the removed skill in the list of job skills
    const countOccurrences = jobSkills.reduce(
      (a, v) => (v.tagId === skill.tagId ? a + 1 : a),
      0
    );
    // if there is just 1 occurence in the list of all job skills remove it from the overall skills list
    if (countOccurrences === 1) {
      const allSkillListFoundIndex =
        this.onboardingStateService.currentResume.skills.findIndex(
          (skillMatch) => skillMatch.tagId === skill.tagId
        );
      if (allSkillListFoundIndex > -1) {
        // remove the skill from all the resume skills
        this.onboardingStateService.currentResume.skills.splice(
          allSkillListFoundIndex,
          1
        );
      }
    }
  }

  /**
   * Get the featured target tiles for HMM sparks skills page
   */
  private getFeaturedTargetTitles(): Observable<FeaturedCategories[]> {
    return this.http
      .get<FeaturedCategories[]>(
        '/targets/GetFeaturedTitles?targetType=JobRole&count=100&alphaSort=true'
      )
      .pipe(
        map((categories: FeaturedCategories[]) => {
          for (const category of categories) {
            category.detailedSkills = category.skills.map(this.makeSkill);
          }
          return categories;
        })
      );
  }

  /** Subscribe to this observable to get the latest user top tags */
  private getUserTopTags(): Observable<any> {
    return this.tagsService.getUserTopTags(false, false, false);
  }

  /**
   * Get a list of similar skills based off of skill names
   * @param inputskills arry of skill names to find similar skills
   * @param currentSkills list of current skills the user already has selected
   */
  private getSimilarSkills(
    inputSkills: string[],
    currentSkills: Skill[]
  ): void {
    // if on a skill search that means no pervious skill has had any similar skills so just get 40
    // always get a least 3 skills per skill passed in, tp account for some returning no results
    const count =
      inputSkills.length >= 11 ? 3 : Math.round(40 / inputSkills.length);
    // NOTE: can have less than 40 skills here
    this.findSimiliarSkills(inputSkills, count, 0.7).subscribe(
      (similarskills: SimilarSkillsResponse) => {
        this.onboardingStateService.setIsSimilarService(
          similarskills.isFromDs3
        );
        // get the first 40 max skills
        const maxSize = 40;
        let similarList = similarskills.tags.slice(
          0,
          similarskills.tags.length >= maxSize
            ? maxSize
            : similarskills.tags.length
        );
        const ids = new Set(currentSkills?.map((d) => d.tagId));
        // filiter out any sills that are the same as already selected
        similarList = similarList.filter((d) => !ids.has(d.tagId));
        // send back the first 10 skills to be displayed right away
        // emit display list, and update the similar tags list without the first 10
        this.onboardingStateService.updateSimilarTags(
          similarList.slice(similarList.length >= 10 ? 10 : similarList.length)
        );
        // this should only update if not on seach
        this.onboardingStateService.updateDisplayedSimilarTags(
          similarList.slice(
            0,
            similarList.length >= 10 ? 10 : similarList.length
          )
        );
      }
    );
  }

  /**
   * Find skills similarities based on a stting list of namess.
   *
   *  The servive will pick a max of 20 sklls to get similar skills from
   *  so results will differ
   * @param skills skill names to find similar skills to
   * @param count  number of similar skills for each passed in skill name
   */
  private findSimiliarSkills(
    skills: string[],
    count: number,
    maximumSimilarity: number = 1
  ): Observable<SimilarSkillsResponse> {
    return this.http
      .get<SimilarSkillsResponse>('/taxonomy/similarskills', {
        params: { skills, count, maximumSimilarity },
      })
      .pipe(catchError((error) => throwError(error)));
  }

  /**
   * Get the featured roles to display.
   * Add in the auth users current role if there is one.
   */
  private getFeaturedDisplayRoles(): Observable<JobRole[]> {
    const authUser = this.authService.authUser;
    // Search for the user's current jobrole. if it is not in the featured list then use the first role that name.
    return combineLatest([
      this.searchOrgRoles(authUser?.viewerProfile?.jobRole),
      this.getFeaturedRoles(),
    ]).pipe(
      map(([currentRoles, roles]) => {
        const anySelected = !!roles.find((role) => {
          return role.selected;
        });
        // add the current role if not in featured
        if (
          !anySelected &&
          authUser?.viewerProfile?.jobRole &&
          currentRoles.length > 0
        ) {
          // get the index of the first role with job role name
          const i = currentRoles.findIndex(
            (role) => role.name === authUser?.viewerProfile?.jobRole
          );
          // If role exists add role to roles to display
          if (i !== -1) {
            let role: JobRole = currentRoles[i];
            role = {
              ...role,
              selected:
                currentRoles[i].name === authUser?.viewerProfile?.jobRole,
              title: role.title,
              resourceType: 'Target',
              description: this.getDescription(currentRoles[i]),
            };
            roles.unshift(role);
            this.onboardingStateService.currentRoleChange(role);
          }
        }
        this.onboardingStateService.featuredRoleTargetsChange(roles);
        return roles;
      })
    );
  }

  /**
   * Create the description for the role join the skills titles
   * @param role current user role
   */
  private getDescription(role: JobRole): string {
    const des = role.skills.map((s) => s).join(', ');
    return des;
  }

  /**
   * Get the featured roles to display
   * Featured roles are from the roles engine marked as "in welcome tour"
   */
  private getFeaturedRoles(): Observable<JobRole[]> {
    return this.targetsService.getFeaturedTitles('JobRole').pipe(
      map((roles: JobRole[]) => {
        const authUser = this.authService.authUser;
        var selectedIndex = null;
        for (var i = 0; i < roles.length; i++) {
          roles[i].featured = true;
          roles[i].resourceType = 'Target';
          if (authUser?.viewerProfile?.jobRole === roles[i].name) {
            if (!selectedIndex) {
              roles[i].selected = true;
              selectedIndex = i;
              this.onboardingStateService.currentRoleChange(roles[i]);
            } else if (
              roles[selectedIndex].skills.length < roles[i].skills.length
            ) {
              roles[i].selected = true;
              roles[selectedIndex].selected = false;
              selectedIndex = i;
              this.onboardingStateService.currentRoleChange(roles[i]);
            }
          } else {
            roles[i].selected = false;
          }
        }
        return roles;
      })
    );
  }

  private normalize(name: string): string {
    return name.toLowerCase().trim();
  }

  /**
   * Create a detailed version of the skill
   * @param name name of the skill
   */
  private makeSkill(name: string): SkillItem {
    return {
      name: name,
      selected: false,
    };
  }
}
